/**
 * Manual-apply column mapping dialog (REQ-7.2).
 *
 * Shown ONLY when at least one template column has no title+type match on the
 * board. For each unmatched column the user chooses: map to an existing
 * same-type column, create it, or skip its values.
 *
 * Create is listed FIRST and preselected: it is the only action that fixes the
 * mismatch for good, by putting the column on the board. Map and Skip resolve
 * THIS apply only — the template is never edited behind the user's back — so both
 * surface a red callout saying the dialog will return until the template and the
 * board match. Create carries no callout.
 *
 * Automations never see this dialog; they skip unmatched columns and notify
 * (REQ-7.4).
 */
import {
  Button,
  Dropdown,
  Modal,
  ModalContent,
  ModalFooter,
  RadioButton,
} from 'monday-ui-react-core';
import React, { useMemo, useState } from 'react';
import { typesMatch } from '../lib/columnMatching';
import './ApplyMappingDialog.css';

/**
 * Warning triangle. Inline rather than Vibe's `Alert` icon because this callout is
 * not a Vibe AttentionBox (see ApplyMappingDialog.css) and needs to inherit the
 * callout's red via currentColor.
 */
function WarningTriangle() {
  return (
    <svg
      className="spt-runwarn__icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3.5 2.5 20h19L12 3.5Z" fill="currentColor" opacity=".18" />
      <path d="M12 3.5 2.5 20h19L12 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 9.5v4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16.7" r="1.05" fill="currentColor" />
    </svg>
  );
}

export default function ApplyMappingDialog({
  show,
  unmatched, // template columns with no board match (single-board form)
  boardColumns,
  usedBoardIds, // board column ids already taken by matched columns
  // The group apply matches TWO boards: `sections` replaces the three props
  // above with one entry per board, [{ key, title, unmatched, boardColumns,
  // usedBoardIds }], and every decision carries its section's key.
  sections,
  onConfirm, // (decisions: [{ section, column, action, targetId? }]) => void
  onCancel,
}) {
  const sectionList = useMemo(
    () => sections || [{ key: 'default', title: null, unmatched: unmatched || [], boardColumns, usedBoardIds }],
    [sections, unmatched, boardColumns, usedBoardIds],
  );
  const candidatesFor = useMemo(() => {
    const bySection = {};
    for (const s of sectionList) {
      const used = new Set([...(s.usedBoardIds || [])].map(String));
      bySection[s.key] = (col) =>
        (s.boardColumns || []).filter((bc) => !used.has(String(bc.id)) && typesMatch(col.type, bc.type));
    }
    return (col, sectionKey) => (bySection[sectionKey] || (() => []))(col);
  }, [sectionList]);

  /**
   * Create is the default (and the first option) because it is the only action
   * that FIXES the mismatch: it puts the column on the board, so every later
   * apply — manual or automation — matches without asking. Map and Skip resolve
   * one run and leave the drift in place. targetId is still prefilled with the
   * best candidate so choosing Map is a single click.
   */
  const [decisions, setDecisions] = useState(() =>
    sectionList.flatMap((s) =>
      (s.unmatched || []).map((col) => {
        const candidates = candidatesFor(col, s.key);
        return {
          section: s.key,
          column: col,
          action: 'create',
          targetId: candidates.length ? candidates[0].id : undefined,
        };
      }),
    ),
  );
  const total = decisions.length;

  const setDecision = (index, patch) =>
    setDecisions((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  return (
    <Modal show={show} onClose={onCancel} title="Resolve columns to apply" width="540px">
      <ModalContent>
        <p className="mb-4 text-sm text-secondary">
          {total} column{total === 1 ? " doesn't" : "s don't"} match{' '}
          {sections ? 'the boards this template applies to' : "this board's subitems"}.
        </p>
        <div className="space-y-4" style={{ maxHeight: '46vh', overflowY: 'auto' }}>
          {sectionList.map((s) => (
            <React.Fragment key={s.key}>
              {s.title && decisions.some((d) => d.section === s.key) && (
                <div className="text-sm font-semibold text-primary">{s.title}</div>
              )}
          {decisions.map((d, i) => {
            if (d.section !== s.key) return null;
            const candidates = candidatesFor(d.column, d.section);
            return (
              <div key={`${d.section}-${d.column.id}`} className="p-4 border border-subtle rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-semibold text-primary">{d.column.title}</span>
                  <span className="text-xs text-secondary font-mono">{d.column.type}</span>
                  <span className="ml-auto px-2 py-0.5 text-xs font-medium text-danger-ink bg-danger-tint rounded">
                    Not found on this board
                  </span>
                </div>
                {/* Order matters: the durable fix first, the one-run choices after. */}
                <div className="space-y-2">
                  <RadioButton
                    text={`Create a new column "${d.column.title}"`}
                    name={`spt-map-${d.section}-${d.column.id}`}
                    checked={d.action === 'create'}
                    onSelect={() => setDecision(i, { action: 'create' })}
                  />
                  <div className="flex items-start gap-2">
                    <RadioButton
                      text="Map to an existing column"
                      name={`spt-map-${d.section}-${d.column.id}`}
                      checked={d.action === 'map'}
                      disabled={!candidates.length}
                      onSelect={() =>
                        setDecision(i, { action: 'map', targetId: d.targetId ?? (candidates[0] && candidates[0].id) })
                      }
                    />
                  </div>
                  {d.action === 'map' && candidates.length > 0 && (
                    <div className="ml-6" style={{ maxWidth: 280 }}>
                      <Dropdown
                        size={Dropdown.size ? Dropdown.size.SMALL : undefined}
                        clearable={false}
                        options={candidates.map((c) => ({ label: `${c.title} (${c.type})`, value: c.id }))}
                        value={{
                          label: (() => {
                            const t = candidates.find((c) => String(c.id) === String(d.targetId));
                            return t ? `${t.title} (${t.type})` : '';
                          })(),
                          value: d.targetId,
                        }}
                        onChange={(opt) => opt && setDecision(i, { targetId: opt.value })}
                      />
                    </div>
                  )}
                  <RadioButton
                    text="Skip this column"
                    name={`spt-map-${d.section}-${d.column.id}`}
                    checked={d.action === 'skip'}
                    onSelect={() => setDecision(i, { action: 'skip' })}
                  />
                </div>
                {/*
                  Map and Skip apply to this run only — the titles still differ
                  afterwards, so the dialog returns on every apply until one side
                  is renamed. Create needs no warning: it removes the mismatch.
                */}
                {(d.action === 'map' || d.action === 'skip') && (
                  <div className="spt-runwarn" role="alert">
                    <WarningTriangle />
                    <div>
                      <span className="spt-runwarn__lead">Applies to this run only.</span>
                      <span className="spt-runwarn__body">
                        Please make sure the template matches the board's columns, or this dialog will
                        reappear.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
            </React.Fragment>
          ))}
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex justify-end gap-3">
          <Button kind={Button.kinds.SECONDARY} onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(decisions)}>Apply template</Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
